import {FormGroup, FormBuilder, Validators} from '@angular/forms';
import {filter, take, takeUntil, distinctUntilChanged, tap} from 'rxjs/operators';
import {GeospatialFilterStore} from '../api/geospatial-filter.store';
import {VisualizationType} from '../model/visualization';
import {Layer, LayerType} from './layer';
import {GeospatialEntitiesConfiguration} from '../model/viewer-config';
import {SpatialEntityData} from '../model/api';
import {MetricFilterControlsStore} from '../control-panel/components/filter/metric-layer-filter.store';

export interface MetricGroupDto {
    key: string;
    label: string;
    enabled: boolean;
    metrics: MetricDto[];
}

export interface VisualizationTypeDto {
    key: VisualizationType;
    label: string;
    enabled: boolean;
}

export interface MetricDto {
    key: string;
    label: string;
    enabled: boolean;
    buckets: BucketDto[];
}

export interface BucketDto {
    key: string;
    labelList: string[];
    colour: string;
}

export class MetricLayer extends Layer {
    mapData: SpatialEntityData[] = [];
    metric: MetricDto;

    layerForm: FormGroup;
    filterControlsStore: MetricFilterControlsStore;

    static defaultName = 'New Metric Layer';
    constructor(public fb: FormBuilder, public geospatialFilterStore: GeospatialFilterStore) {
        super(MetricLayer.defaultName, LayerType.METRIC, fb, geospatialFilterStore);
        this.metric = {
            key: '',
            label: '',
            enabled: false,
            buckets: [],
        };

        // Create a new FilterControlsStore instance
        this.filterControlsStore = new MetricFilterControlsStore(fb, geospatialFilterStore);

        // Initialize the form
        this.initForm();

        // Listen for changes in the component's metricLayerForm and store filters
        this.subscribeToFormChanges();
    }

    initForm(): void {
        this.layerForm = this.fb.group({
            visible: [true],
            name: [this.name, Validators.required],
            measure: [null, Validators.required], // type: MetricGroupDto
            metric: [{value: null, disabled: true}, Validators.required], // type: MetricDto
            categoriesSelected: [null],
            visualizationType: [null, Validators.required],
            renderedVisualizationType: [null],
        });

        // Merge with FilterControlsStore's formGroup (filters)
        // Check if the filters form group is ready before adding it
        const filtersFormGroup = this.filterControlsStore.filtersFormGroup;

        // Add the filters form group only if it exists
        if (filtersFormGroup) {
            this.layerForm.addControl('filters', filtersFormGroup);
        } else {
            // If filtersFormGroup is not ready yet, wait until it is initialized
            this.filterControlsStore.filtersFormGroupChanged$
                .pipe(
                    tap(() => this.filterLoading.next(true)),
                    filter((initialised) => !!initialised),
                    take(1),
                )
                .subscribe(() => {
                    this.layerForm.addControl('filters', this.filterControlsStore.filtersFormGroup, {
                        emitEvent: false,
                    });
                    this.filterLoading.next(false);
                });
        }
    }

    updateName(newName: string) {
        this.name = newName;
        this.layerForm.get('name').setValue(newName);
    }

    toggleVisibility(): void {
        this.visible = !this.visible;
        this.layerForm.get('visible').setValue(this.visible);
    }

    subscribeToFormChanges(): void {
        this.layerForm
            .get('measure')
            .valueChanges.pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
            .subscribe((measure: MetricGroupDto) => {
                this.updateName(this.configureMetricLayerName(measure));

                if (measure) {
                    this.layerForm.get('metric').enable();
                } else {
                    this.layerForm.get('metric').disable();
                }
                this.layerForm.get('metric').reset(null, {emitEvent: true});

                //Full reset categories
                this.resetCategories();
            });

        this.layerForm
            .get('metric')
            .valueChanges.pipe(
                takeUntil(this.unsubscribe$),
                distinctUntilChanged(),
                filter((value) => !!value),
            )
            .subscribe((metric: MetricDto) => {
                this.updateName(this.configureMetricLayerName(this.layerForm.value.measure, metric));
                this.metric = metric;

                //reset category filter to default for this valueType
                this.resetCategories(metric.buckets);
            });
    }

    public configureMetricLayerName(measure: MetricGroupDto, metric?: MetricDto): string {
        if (!measure || !measure.label) return MetricLayer.defaultName;
        if (!metric) return measure?.label;
        return `${measure?.label}: ${metric.label}`;
    }

    getFilters() {
        return this.filterControlsStore.currentFilters;
    }

    getMeasure() {
        return this.layerForm.value.measure?.key;
    }

    getMetric(): string {
        return this.layerForm.value.metric?.key;
    }

    getMetricDto(): MetricDto {
        return this.metric;
    }

    getValueType() {
        return this.layerForm.value.metric?.valueType;
    }

    getVisualizationType() {
        return this.layerForm.value.visualizationType;
    }

    getRenderedVisualizationType() {
        return this.layerForm.value?.renderedVisualizationType || this.getVisualizationType();
    }

    resetCategories(buckets?: BucketDto[]) {
        this.updateCategoriesSelected(buckets);

        this.refresh.next(true);
    }

    updateMapDataSource(mapDataSource: GeospatialEntitiesConfiguration) {
        this.mapData = mapDataSource.data;
    }

    public updateSelectedMeasure(measure: MetricGroupDto, emitEvent: boolean): void {
        this.layerForm.get('measure').setValue(measure, {emitEvent: emitEvent});
    }

    updateCategoriesSelected(buckets: BucketDto[]) {
        this.layerForm.get('categoriesSelected').setValue(this.metric.buckets ? buckets : null);
    }

    getBuckets(categoriesSelected = this.layerForm.value.categoriesSelected): string[] {
        const arr: string[] = [];
        categoriesSelected?.forEach((bucket: BucketDto) => {
            arr.push(bucket.key);
        });
        return arr;
    }

    showCategorySection(): boolean {
        return this.metric.buckets?.length > 0;
    }

    updateRenderedVisualizationType(visualizationType: VisualizationType) {
        this.layerForm.get('renderedVisualizationType').setValue(visualizationType, {emitEvent: false});
    }

    clearFilters(): void {
        this.filterControlsStore.resetQuickFilters();
        this.filterControlsStore.resetGeospatialFilters();
    }

    layerCanBeDisplayed(): boolean {
        return this.getVisualizationType() && this.getMetric() && super.layerCanBeDisplayed();
    }

    copy(layer: MetricLayer): void {
        super.copy(layer);
        // We need to ensure the layerForm is initialized before performing an operation on it
        this.filterLoading$
            .pipe(
                filter((loading) => !loading),
                take(1),
            )
            .subscribe(() => {
                this.layerForm.patchValue(layer.layerForm.value);
                this.metric = layer.metric;
            });
    }
}
